import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { DialogModalData } from '../dialog-modal/dialog-modal.component';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';

@Component({
  selector: 'app-modal',
  templateUrl: './business-unification-details-modal.component.html',
  styleUrls: ['./business-unification-details-modal.component.scss'],
})
/**
 * Modal for business unification details
 */
export class BusinessUnificationDetailsModalComponent implements OnInit {
  labels = {
    savingError: 'unexpected-error',
    rejectConfirm: 'business-unification-reject-confirm',
    rejectConfirmMessage: 'business-unification-reject-confirm-message',
    rejectWithSuccess: 'business-unification-reject-success',
    rejectError: 'business-unification-reject-error',
    acceptConfirm: 'business-unification-accept-confirm',
    acceptConfirmMessage: 'business-unification-accept-confirm-message',
    acceptWithSuccess: 'business-unification-accept-success',
    acceptError: 'business-unification-accept-error',
    confirm: 'confirm',
    cancel: 'cancel',
  };

  loading: boolean = false;

  businessUnification: any;

  /**
   * @constructor
   */
  constructor(
    public dialogRef: MatDialogRef<BusinessUnificationDetailsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private changeDetectorRef: ChangeDetectorRef,
    private snackBarService: SnackBarService,
    private translationService: TranslationService,
    private readonly dialogService: DialogService
  ) {}

  /**
   * On Init
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);

    this.getBusinessUnificationDetails();
  }

  /**
   * Action to close the dialog
   */
  onCloseClick() {
    this.dialogRef.close();
  }

  /**
   * Retrieve the details of a business unification request
   */
  getBusinessUnificationDetails() {
    this.userService.getBusinessUnificationDetails(this.data.id).subscribe({
      next: (response) => {
        this.businessUnification = response;
      },
    });
  }

  /**
   * Accept business unification by id
   * @param {number} id
   */
  acceptUnification(id: number) {
    const dialogData: DialogModalData = {
      title: this.labels.acceptConfirm,
      secondaryContent: [
        {
          content: this.labels.acceptConfirmMessage,
          style: {
            color: 'var(--Cinzas-400, #19212B)',
            fontFamily: 'Helvetica',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '125%',
          },
        },
      ],
      successButtonText: this.labels.confirm,
      failButtonText: this.labels.cancel,
    };
    this.dialogService.openDialog(
      dialogData,
      () => {
        this.userService.acceptBusinessUnificationRequest(id).subscribe({
          next: () => {
            this.snackBarService.success(this.labels.acceptWithSuccess);
            this.dialogRef.close({
              reload: true,
            });
          },
          error: () => {
            this.snackBarService.error(this.labels.acceptError);
          },
        });
      },
      () => {}
    );
  }

  /**
   * Reject a business unification request by id
   * @param {number} id
   */
  rejectUnification(id: number) {
    const dialogData: DialogModalData = {
      title: this.labels.rejectConfirm,
      secondaryContent: [
        {
          content: this.labels.rejectConfirmMessage,
          style: {
            color: 'var(--Cinzas-400, #19212B)',
            fontFamily: 'Helvetica',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '125%',
          },
        },
      ],
      successButtonText: this.labels.confirm,
      failButtonText: this.labels.cancel,
    };
    this.dialogService.openDialog(
      dialogData,
      () => {
        this.userService.rejectBusinessUnificationRequest(id).subscribe({
          next: () => {
            this.snackBarService.success(this.labels.rejectWithSuccess);
            this.dialogRef.close({
              reload: true,
            });
          },
          error: () => {
            this.snackBarService.error(this.labels.rejectError);
          },
        });
      },
      () => {}
    );
  }

  /**
   * Count final total representatives
   * @return {number}
   */
  countFinalTotalRepresentatives(): number {
    let total = 0;

    for (const businessOrigin of this.businessUnification.businessesOrigins) {
      total += businessOrigin.totalRepresentatives;
    }

    return total;
  }

  /**
   * Count final total equipments
   * @return {number}
   */
  countFinalTotalEquipments(): number {
    let total = 0;

    for (const businessOrigin of this.businessUnification.businessesOrigins) {
      total += businessOrigin.totalEquipments;
    }

    return total;
  }

  /**
   * Define Business Role
   * @param {number} id
   * @return {string}
   */
  defineBusinessRole(id: number): string {
    if (this.businessUnification.businessDestination.id === id) {
      return 'Destination';
    }
    return 'Origin';
  }

  /**
   * Returns current language
   * @return {string}
   */
  getLanguage() {
    return this.translationService.getActiveLang();
  }
}
